import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthService';
import ProfileService from '../services/ProfileService';
import Home from '../pages/Home';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';

const HomeRedirect = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileCheckLoading, setProfileCheckLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(false);

  useEffect(() => {
    const checkProfileAndRedirect = async () => {
      // Only proceed if authentication state is determined and user is authenticated
      if (!isLoading && isAuthenticated && user) {
        setProfileCheckLoading(true);
        setRateLimitError(false);
        
        try {
          const profileService = new ProfileService(user);
          const profile = await profileService.getRecordsByUserId(user.sub);
          
          if (profile && profile.isCompleted) {
            // Profile exists and is complete, redirect to dashboard
            navigate('/dashboard');
          } else {
            // Profile doesn't exist or is incomplete, redirect to onboarding
            const returnTo = `${location.pathname}${location.search || ''}`;
            navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`);
          }
        } catch (error) {
          console.error('Error checking profile:', error);
          // Handle rate limit errors
          if (error?.status === 429) {
            setRateLimitError(true);
            return;
          }
          // On other errors, redirect to onboarding to be safe
          const returnTo = `${location.pathname}${location.search || ''}`;
          navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`);
        } finally {
          setProfileCheckLoading(false);
        }
      }
    };

    checkProfileAndRedirect();
  }, [isAuthenticated, isLoading, user, navigate, location.pathname, location.search]);

  // Show loading spinner while authentication state is being determined or profile is being checked
  if (isLoading || profileCheckLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Show error if system is rate limited
  if (rateLimitError) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        p={2}
      >
        <Alert severity="warning" sx={{ maxWidth: 'sm', borderRadius: 2 }}>
          <AlertTitle sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
            System Temporarily Unavailable
          </AlertTitle>
          We're experiencing high demand at the moment. Please try again in a few moments.
          <Box mt={2}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // If not authenticated, show the Home component
  if (!isAuthenticated) {
    return <Home />;
  }

  // This should not be reached due to the useEffect redirect,
  // but return null as fallback
  return null;
};

export default HomeRedirect;